import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {AsyncPipe} from '@angular/common';
import {Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule,} from '@angular/forms';
import {MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger,} from '@angular/material/autocomplete';
import {MatChipGrid, MatChipInput, MatChipRemove, MatChipRow} from '@angular/material/chips';
import {MatOption} from '@angular/material/core';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {User, UserService} from '@app/shared/user.service';
import {Observable} from 'rxjs';
import {map, startWith, take} from 'rxjs/operators';

@Component({
    selector: 'frog-user-picker',
    templateUrl: './user-picker.component.html',
    styleUrls: ['./user-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: UserPickerComponent,
        },
    ],
    imports: [
        MatFormField,
        MatLabel,
        MatChipGrid,
        MatChipRow,
        MatChipRemove,
        MatIcon,
        MatInput,
        ReactiveFormsModule,
        MatAutocompleteTrigger,
        MatChipInput,
        MatAutocomplete,
        MatOption,
        AsyncPipe,
    ],
})
export class UserPickerComponent implements OnInit, ControlValueAccessor {
    @Input() multiple: boolean = true;
    @Input() label: string;
    @ViewChild('userInput') userInput: ElementRef<HTMLInputElement>;
    separatorKeysCodes: number[] = [ENTER, COMMA];
    userControl: FormControl;
    filteredUsers$: Observable<User[]>;
    users: User[];
    allUsers: User[];

    onChange = (User) => {};
    onTouched = () => {};
    touched = false;
    disabled = false;

    constructor(private userService: UserService) {
        this.users = [];
        this.allUsers = [];
        this.userControl = new FormControl('');
    }

    writeValue(users: string[]): void {
        if (users !== null) {
            const userMap = {};
            this.allUsers.forEach((user) => (userMap[user.email] = user));
            this.users = users.map((email) => userMap[email]);
        }
    }
    registerOnChange(callback: any): void {
        this.onChange = callback;
    }
    registerOnTouched(callback: any): void {
        this.onTouched = callback;
    }
    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    ngOnInit(): void {
        this.filteredUsers$ = this.userControl.valueChanges
            .pipe(
                startWith(''),
                map((value: string) => this.filter(value)),
            );

        this.userService.users
            .pipe(
                take(1)
            )
            .subscribe((users) => {
                this.allUsers = users.slice();
            });
    }

    remove(user: User): void {
        const index = this.users.indexOf(user);
        if (index >= 0) {
            this.markAsTouched();
            this.users.splice(index, 1);
            this.onChange(this.users);
        }
    }

    selected(event: MatAutocompleteSelectedEvent): void {
        const user = event.option.value;
        const index = this.users.indexOf(user);
        if (index === -1) {
            this.markAsTouched();
            this.users.push(event.option.value);
            if (this.multiple) {
                this.onChange(this.users);
            } else {
                this.onChange(user);
            }
        }
    }

    convert(user: User): string {
        return user.name;
    }

    private filter(value: string): User[] {
        const query = value?.toLowerCase();
        return this.allUsers.filter(user => `${user.name}${user.email}`.toLowerCase().includes(query));
    }

    private markAsTouched(): void {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }
}
